import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircleIcon } from 'lucide-react'
import { Slot } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  `
    inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium
    whitespace-nowrap transition-all outline-none
    focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50
    disabled:pointer-events-none disabled:opacity-50
    aria-invalid:border-destructive aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    [&_svg:not([class*='size-'])]:size-4
  `,
  {
    variants: {
      variant: {
        default: `
          bg-primary text-primary-foreground
          hover:bg-primary/90
        `,
        destructive: `
          bg-destructive text-white
          hover:bg-destructive/90
          focus-visible:ring-destructive/20
          dark:bg-destructive/60 dark:focus-visible:ring-destructive/40
        `,
        outline: `
          border bg-background shadow-xs
          hover:bg-accent hover:text-accent-foreground
          dark:border-input dark:bg-input/30 dark:hover:bg-input/50
        `,
        secondary: `
          bg-secondary text-secondary-foreground
          hover:bg-secondary/80
        `,
        ghost: `
          hover:bg-accent hover:text-accent-foreground
          dark:hover:bg-accent/50
        `,
        link: `
          text-primary underline-offset-4
          hover:underline
        `,
      },
      size: {
        default: `
          h-9 px-4 py-2
          has-[>svg]:px-3
        `,
        sm: `
          h-8 gap-1.5 rounded-md px-3
          has-[>svg]:px-2.5
        `,
        lg: `
          h-10 rounded-md px-6
          has-[>svg]:px-4
        `,
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

interface ButtonLoadingProps extends ButtonProps {
  isLoading?: boolean
}

function ButtonLoading({ isLoading, className, children, ...props }: ButtonLoadingProps) {
  return (
    <Button
      {...props}
      disabled={isLoading}
      className={cn('relative', className)}
      data-state={isLoading ? 'loading' : undefined}
    >
      <LoaderCircleIcon
        data-slot='loader'
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0',
          isLoading && 'animate-spin opacity-100'
        )}
      />
      {isLoading && (
        <LoaderCircleIcon
          className={`
            absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin
            text-muted-foreground
          `}
        />
      )}
      <span className={isLoading ? 'text-transparent' : ''}>{children}</span>
    </Button>
  )
}

export { Button, buttonVariants, ButtonLoading }
